import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  inject
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { GetInTouchFormComponent } from '../get-in-touch-form/get-in-touch-form.component';
import { SeoMetadataService } from '../../core';
import {
  CAROUSEL_IMAGES,
  STATS,
  PS_CARDS,
  FEATURES,
  WHY_ITEMS,
  CONSOLIDATED_FEATURES,
  COMPARISON_ROWS,
  WORKFLOW_STEPS,
  SECURITY_ITEMS
} from './landing-dashboard.data';

@Component({
  selector: 'app-landing-dashboard',
  templateUrl: './landing-dashboard.component.html',
  styleUrls: ['./landing-dashboard.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, GetInTouchFormComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LandingDashboardComponent implements OnInit, AfterViewInit {
  private readonly router = inject(Router);
  private readonly seoMetadataService = inject(SeoMetadataService);

  @ViewChild('getInTouchFormDialog')
  getInTouchForm?: GetInTouchFormComponent;
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly observers: IntersectionObserver[] = [];
  private autoSlideInterval: ReturnType<typeof setInterval> | null = null;

  // Slider refs
  @ViewChild('sliderContainer', { static: false })
  sliderContainerRef?: ElementRef<HTMLElement>;
  @ViewChild('slider', { static: false }) sliderRef?: ElementRef<HTMLElement>;

  slideWidth = 400;
  menuOpen = false;
  isMobile = false;

  // Scroll-reveal visibility flags
  heroVisible = false;
  statsAnimated = false;
  psVisible = false;
  featuresVisible = false;
  platformVisible = false;
  whyVisible = false;
  compVisible = false;
  workflowVisible = false;
  securityVisible = false;

  // Section refs
  @ViewChild('statsSection', { static: false }) statsSection!: ElementRef;
  @ViewChild('psSection', { static: false }) psSection!: ElementRef;
  @ViewChild('featuresSection', { static: false }) featuresSection!: ElementRef;
  @ViewChild('platformSection', { static: false }) platformSection!: ElementRef;
  @ViewChild('whySection', { static: false }) whySection!: ElementRef;
  @ViewChild('compSection', { static: false }) compSection!: ElementRef;
  @ViewChild('workflowSection', { static: false })
  workflowSection!: ElementRef;
  @ViewChild('securitySection', { static: false })
  securitySection!: ElementRef;

  // Static data
  readonly images = CAROUSEL_IMAGES;
  readonly stats = STATS;
  readonly psCards = PS_CARDS;
  readonly features = FEATURES;
  readonly whyItems = WHY_ITEMS;
  readonly consolidatedFeatures = CONSOLIDATED_FEATURES;
  readonly comparisonRows = COMPARISON_ROWS;
  readonly workflowSteps = WORKFLOW_STEPS;
  readonly securityItems = SECURITY_ITEMS;

  public currentIndex = 0;

  ngOnInit(): void {
    this.setSeoMetadata();
    this.isMobile = globalThis.innerWidth <= 430;
    this.positionSlides();
    this.startAutoSlide();
    setTimeout(() => {
      this.heroVisible = true;
      this.cdr.markForCheck();
    }, 100);
  }

  private setSeoMetadata(): void {
    this.seoMetadataService.setSeoMetadata({
      title: 'GemFin | Smarter Way to Manage Mutual Fund Operations',
      description:
        'Manage mutual fund operations with GemFin. Trusted by brokers and wealth managers, GemFin offers rapid onboarding, white-glove support, enterprise-grade reliability, and a faster path to go live.',
      url: 'https://gemfin.in/#/dashboard',
      keywords:
        'GemFin, mutual fund operations, wealth management platform, MFD software, broker platform, investment management, mutual fund technology, wealth advisory platform'
    });
  }

  ngAfterViewInit(): void {
    this.setupScrollObservers();
  }

  private setupScrollObservers(): void {
    const sectionMap: { ref: ElementRef | undefined; flag: string }[] = [
      { ref: this.statsSection, flag: 'statsAnimated' },
      { ref: this.psSection, flag: 'psVisible' },
      { ref: this.featuresSection, flag: 'featuresVisible' },
      { ref: this.platformSection, flag: 'platformVisible' },
      { ref: this.whySection, flag: 'whyVisible' },
      { ref: this.compSection, flag: 'compVisible' },
      { ref: this.workflowSection, flag: 'workflowVisible' },
      { ref: this.securitySection, flag: 'securityVisible' }
    ];

    for (const item of sectionMap) {
      if (!item.ref) {
        continue;
      }
      const obs = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.setVisibilityFlag(item.flag, true);
            obs.disconnect();
          }
        },
        { threshold: 0.15 }
      );
      obs.observe(item.ref.nativeElement);
      this.observers.push(obs);
    }

    this.destroyRef.onDestroy(() => {
      this.observers.forEach((o) => o.disconnect());
      this.stopAutoSlide();
    });
  }

  private setVisibilityFlag(flag: string, value: boolean): void {
    switch (flag) {
      case 'statsAnimated':
        this.statsAnimated = value;
        break;
      case 'psVisible':
        this.psVisible = value;
        break;
      case 'featuresVisible':
        this.featuresVisible = value;
        break;
      case 'platformVisible':
        this.platformVisible = value;
        break;
      case 'whyVisible':
        this.whyVisible = value;
        break;
      case 'compVisible':
        this.compVisible = value;
        break;
      case 'workflowVisible':
        this.workflowVisible = value;
        break;
      case 'securityVisible':
        this.securityVisible = value;
        break;
    }
    this.cdr.markForCheck();
  }

  private startAutoSlide(): void {
    this.autoSlideInterval = setInterval(() => {
      this.plusSlides(1);
    }, 5000);
  }

  private stopAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
      this.autoSlideInterval = null;
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = globalThis.innerWidth <= 430;
    this.positionSlides();
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  positionSlides(): void {
    const sliderEl = this.sliderRef?.nativeElement;
    const containerEl = this.sliderContainerRef?.nativeElement;

    if (sliderEl && containerEl) {
      const slides = sliderEl.querySelectorAll<HTMLElement>('.slide');
      const containerWidth = containerEl.offsetWidth;
      const slideWidthWithMargin = 420;
      const centerOffset = containerWidth / 2 - slideWidthWithMargin / 2;
      const slideOffset = this.currentIndex * slideWidthWithMargin;
      const transformValue = centerOffset - slideOffset;

      sliderEl.style.transform = `translateX(${transformValue}px)`;

      slides.forEach((slide, index) => {
        const isActive = index === this.currentIndex;
        slide.classList.toggle('active', isActive);
        slide.style.transform = isActive ? 'scale(1)' : 'scale(0.8)';
        slide.style.opacity = isActive ? '1' : '0.6';
      });
    }
  }

  plusSlides(direction: number): void {
    this.updateCurrentIndex(this.currentIndex + direction);
  }

  currentSlide(index: number): void {
    this.updateCurrentIndex(index);
  }

  updateCurrentIndex(newIndex: number): void {
    const totalSlides = this.images.length;
    if (newIndex < 0) {
      this.currentIndex = totalSlides - 1;
    } else if (newIndex >= totalSlides) {
      this.currentIndex = 0;
    } else {
      this.currentIndex = newIndex;
    }
    this.positionSlides();
  }

  login(): void {
    const loginUrl = `${globalThis.location.origin}/#/login`;
    globalThis.open(loginUrl, '_blank');
  }

  preventRedirect(event: Event): void {
    event.preventDefault();
  }

  scrollTo(id: string): void {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  openGetInTouchModal(): void {
    this.getInTouchForm?.openDialog();
  }
}
